/**
 * Maps beatly-creator-api content payloads to card strip items (parity with beatly-discovery-app).
 */

import type { V1CreatorContent } from '@/api/creatorApi';
import { getImageProxyUrl } from '@/api/creatorApi';
import i18n from '@/i18n';

export interface CreatorCardContent {
  image: string;
  imageProxyUrl?: string;
  title: string;
  likes: string;
  views: string;
  comments?: string;
  datePublished?: string;
  videoId?: string;
  embedUrl?: string;
  permalink?: string | null;
  platform?: 'tiktok' | 'instagram' | 'youtube' | 'twitter';
}

function formatMetric(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(Math.round(n));
}

export function createPlaceholderContent(count: number = 5): CreatorCardContent[] {
  return Array.from({ length: count }, () => ({
    image: '',
    title: '',
    likes: '',
    views: '',
    permalink: null,
  }));
}

function generateContentTitle(datePublished: string | undefined): string {
  if (!datePublished) return i18n.t('campaigns.profiles.contentPostedRecent');
  try {
    const date = new Date(datePublished);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return i18n.t('campaigns.profiles.contentPostedToday');
    if (diffDays === 1) return i18n.t('campaigns.profiles.contentPostedYesterday');
    if (diffDays < 7) return i18n.t('campaigns.profiles.contentPostedDaysAgo', { count: diffDays });
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return i18n.t('campaigns.profiles.contentPostedWeeksAgo', { count: weeks });
    }
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    return date.toLocaleDateString(lng, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return i18n.t('campaigns.profiles.contentPostedRecent');
  }
}

function extractPlatform(
  permalink?: string | null,
  creatorPlatform?: string
): 'tiktok' | 'instagram' | 'youtube' | 'twitter' | undefined {
  if (permalink) {
    const url = permalink.toLowerCase();
    if (url.includes('tiktok.com')) return 'tiktok';
    if (url.includes('instagram.com')) return 'instagram';
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'twitter';
  }
  if (creatorPlatform) {
    const platform = creatorPlatform.toLowerCase();
    if (platform === 'tiktok') return 'tiktok';
    if (platform === 'instagram') return 'instagram';
    if (platform === 'youtube') return 'youtube';
    if (platform === 'twitter') return 'twitter';
  }
  return undefined;
}

export function transformContentToCardFormat(
  apiContent: V1CreatorContent,
  creatorPlatform?: string,
  token?: string | null
): CreatorCardContent {
  const rawPlatform = apiContent.platform || extractPlatform(apiContent.permalink, creatorPlatform);
  let platform: 'tiktok' | 'instagram' | 'youtube' | 'twitter' | undefined;
  if (rawPlatform) {
    const normalized = rawPlatform.toLowerCase();
    if (normalized === 'tiktok' || normalized === 'instagram' || normalized === 'youtube' || normalized === 'twitter') {
      platform = normalized;
    }
  }

  let videoId: string | undefined;
  let embedUrl: string | undefined;

  if (apiContent.embed_url) {
    embedUrl = apiContent.embed_url;
    if (platform === 'tiktok') {
      const match = apiContent.embed_url.match(/\/video\/(\d+)/);
      if (match) videoId = match[1];
    } else if (platform === 'youtube') {
      const match = apiContent.embed_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) videoId = match[1];
    }
  } else if (apiContent.permalink) {
    if (platform === 'tiktok') {
      const match = apiContent.permalink.match(/\/video\/(\d+)/);
      if (match) {
        videoId = match[1];
        embedUrl = apiContent.permalink;
      }
    } else if (platform === 'youtube') {
      const match = apiContent.permalink.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) {
        videoId = match[1];
        embedUrl = apiContent.permalink;
      }
    } else if (platform === 'instagram' || platform === 'twitter') {
      embedUrl = apiContent.permalink;
    }
  }

  const placeholder = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=300&fit=crop';
  let imageUrl: string;
  let imageProxyUrl: string | undefined;

  if (apiContent.preview_url && apiContent.preview_url.trim() !== '') {
    imageUrl = apiContent.preview_url;
    imageProxyUrl = getImageProxyUrl(apiContent.preview_url, token) || undefined;
  } else {
    imageUrl = placeholder;
  }

  const datePub = apiContent.date_published ?? '';
  const title = apiContent.title || generateContentTitle(datePub);

  return {
    image: imageUrl,
    imageProxyUrl,
    title,
    likes: apiContent.likes != null && apiContent.likes >= 0 ? formatMetric(apiContent.likes) : '',
    views: apiContent.views != null && apiContent.views >= 0 ? formatMetric(apiContent.views) : '',
    comments: apiContent.comments != null && apiContent.comments >= 0 ? formatMetric(apiContent.comments) : '',
    datePublished: apiContent.date_published,
    videoId,
    embedUrl,
    permalink: apiContent.permalink || null,
    platform,
  };
}

export function transformContentArrayToCardFormat(
  apiContents: V1CreatorContent[],
  creatorPlatform?: string,
  token?: string | null
): CreatorCardContent[] {
  return apiContents.map((c) => transformContentToCardFormat(c, creatorPlatform, token));
}
